import {
  DATA_ONLY_DEFAULT_PATH,
  canAccessConversationViews,
} from '../components/Auth/Text/access'
import { canAccessInsights, isInsightsPathname } from './insightsRouteState'
import { canAccessLeasingSpecials } from '../lib/leasingSpecialsAccess'
export { CHROME_COMPANION_READY_MESSAGE_TYPE } from './chromeCompanionProtocol'

const SESSION_ENTRY_PATHS = new Set(['/', '/login', '/onboarding'])
const MOBILE_ALLOWED_AUTH_ROUTES = ['/login', '/my-account', '/tasks', '/resident-conversations', '/chrome', '/leasing-specials']
// The Chrome companion applies its own Tasks/Insights guard after this check.
const TASKS_ONLY_ALLOWED_PATHS = ['/tasks', '/my-account', '/chrome', '/leasing-specials']
const RETIRED_SITE_VISIT_PATH = '/site-visit'
const SITE_VISIT_COMMUNITY_THREADS_PATH = /^\/site-visit\/community\/(\d+)\/threads\/?$/

export const CHROME_COMPANION_BASE_PATH = '/chrome'
export const CHROME_COMPANION_INSIGHTS_PATH = `${CHROME_COMPANION_BASE_PATH}/insights`
export const CHROME_COMPANION_TASKS_PATH = `${CHROME_COMPANION_BASE_PATH}/tasks`
export const CHROME_COMPANION_SPECIALS_PATH = `${CHROME_COMPANION_BASE_PATH}/leasing-specials`
export const CHROME_COMPANION_EMBED_PARAM = 'embed'
export const CHROME_COMPANION_EMBED_VALUE = 'chrome-side-panel'
export const CHROME_COMPANION_SOURCE_PARAM = 'source'
export const CHROME_COMPANION_SOURCE_VALUE = 'chrome-side-panel'

const getRetiredSiteVisitRedirect = (pathname = '') => {
  if (pathname !== RETIRED_SITE_VISIT_PATH && !pathname.startsWith(`${RETIRED_SITE_VISIT_PATH}/`)) {
    return null
  }

  const communityThreadsMatch = pathname.match(SITE_VISIT_COMMUNITY_THREADS_PATH)
  if (communityThreadsMatch) {
    return {
      pathname: `/text/conversations/portfolio/${communityThreadsMatch[1]}`,
    }
  }

  return {
    pathname: '/insights',
  }
}

const isConversationPathname = (pathname = '') =>
  pathname === '/text/conversations'
  || pathname.startsWith('/text/conversations/')
  || pathname.startsWith('/text/insights-conversations/')

export const isAuthFreePathname = (pathname = '') =>
  pathname === '/reset-password'
  || pathname === '/brand-hub'
  || pathname === '/public/changelog'
  || pathname.startsWith('/maps/')

export const isSessionEntryPathname = (pathname = '') =>
  SESSION_ENTRY_PATHS.has(pathname)

export const authenticatedRouteAllowsMobile = (pathname = '') =>
  MOBILE_ALLOWED_AUTH_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`),
  )

export const canTasksOnlyUserOpenPath = (user, pathname = '') =>
  !user?.isTasksOnlyUser || TASKS_ONLY_ALLOWED_PATHS.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`),
  )

export const isChromeCompanionPathname = (pathname = '') =>
  pathname === CHROME_COMPANION_BASE_PATH
  || pathname.startsWith(`${CHROME_COMPANION_BASE_PATH}/`)

export const hasChromeCompanionEmbed = (search = '') =>
  new URLSearchParams(search).get(CHROME_COMPANION_EMBED_PARAM) === CHROME_COMPANION_EMBED_VALUE

/**
 * The extension identifies its embedded request with a narrow URL contract.
 * The embed value controls the layout; source is telemetry only and must not
 * become an authorization signal.
 */
export const isChromeCompanionRequest = ({ pathname = '', search = '' } = {}) => {
  if (!isChromeCompanionPathname(pathname)) return false
  return hasChromeCompanionEmbed(search)
}

export const getChromeCompanionActivitySource = ({ pathname = '', search = '' } = {}) => {
  if (!isChromeCompanionRequest({ pathname, search })) return null

  const params = new URLSearchParams(search)
  return params.get(CHROME_COMPANION_SOURCE_PARAM) === CHROME_COMPANION_SOURCE_VALUE
    ? CHROME_COMPANION_SOURCE_VALUE
    : null
}

export const isChromeCompanionInsightsPathname = (pathname = '') =>
  pathname === CHROME_COMPANION_INSIGHTS_PATH
  || pathname.startsWith(`${CHROME_COMPANION_INSIGHTS_PATH}/`)

/**
 * Map a companion URL to the corresponding full-app route.
 *
 * Companion-only query parameters are intentionally removed from this link;
 * portfolio and dashboard state remains available when the user opens the
 * regular ResiDesk application.
 */
export const getChromeCompanionNormalPath = ({ pathname = '', search = '' } = {}) => {
  const normalPathname = pathname === CHROME_COMPANION_SPECIALS_PATH ? '/leasing-specials' : isChromeCompanionInsightsPathname(pathname)
    ? `/insights${pathname.slice(CHROME_COMPANION_INSIGHTS_PATH.length)}`
    : '/tasks'
  const params = new URLSearchParams(search)

  params.delete(CHROME_COMPANION_EMBED_PARAM)
  params.delete(CHROME_COMPANION_SOURCE_PARAM)

  const normalSearch = params.toString()
  return `${normalPathname}${normalSearch ? `?${normalSearch}` : ''}`
}

/**
 * Keep normal ResiDesk routes out of the embedded frame. They retain the
 * application's regular frame protections and should open in a browser tab.
 */
export const getChromeCompanionExternalDestination = ({
  href = '',
  currentOrigin = '',
} = {}) => {
  try {
    const destination = new URL(href, currentOrigin)

    if (destination.origin !== currentOrigin || isChromeCompanionPathname(destination.pathname)) {
      return null
    }

    destination.searchParams.delete(CHROME_COMPANION_EMBED_PARAM)
    destination.searchParams.delete(CHROME_COMPANION_SOURCE_PARAM)

    return `${destination.pathname}${destination.search}${destination.hash}`
  } catch {
    return null
  }
}

export const getChromeCompanionInPanelPath = ({
  href = '',
  currentOrigin = '',
  companionSearch = '',
} = {}) => {
  try {
    const destination = new URL(href, currentOrigin)
    if (destination.origin !== currentOrigin) return null

    if (destination.pathname === '/insights' || destination.pathname.startsWith('/insights/')) {
      destination.pathname = `${CHROME_COMPANION_INSIGHTS_PATH}${destination.pathname.slice('/insights'.length)}`
    } else if (destination.pathname === '/tasks') {
      destination.pathname = CHROME_COMPANION_TASKS_PATH
    } else if (destination.pathname === '/leasing-specials') {
      destination.pathname = CHROME_COMPANION_SPECIALS_PATH
    } else {
      return null
    }

    destination.searchParams.set(CHROME_COMPANION_EMBED_PARAM, CHROME_COMPANION_EMBED_VALUE)
    if (getChromeCompanionActivitySource({
      pathname: CHROME_COMPANION_BASE_PATH,
      search: companionSearch,
    })) {
      destination.searchParams.set(CHROME_COMPANION_SOURCE_PARAM, CHROME_COMPANION_SOURCE_VALUE)
    } else {
      destination.searchParams.delete(CHROME_COMPANION_SOURCE_PARAM)
    }

    return `${destination.pathname}${destination.search}${destination.hash}`
  } catch {
    return null
  }
}

export const getDefaultAuthenticatedPath = (user) => {
  if (!canAccessInsights(user)) {
    return '/tasks'
  }

  if (
    user?.defaultAuthenticatedPath === '/tasks' ||
    user?.defaultAuthenticatedPath === '/insights' ||
    user?.defaultAuthenticatedPath === '/text/conversations'
  ) {
    return user.defaultAuthenticatedPath
  }

  const shouldRouteToInsights =
    user?.isAdmin
    || user?.isGhostUser
    || user?.isDataOnlyUser
    || user?.managesMultipleCommunities

  if (user?.defaultToTasks) {
    return '/tasks'
  }

  return shouldRouteToInsights ? '/insights' : '/text/conversations'
}

export const getChromeCompanionPath = (user) => {
  const defaultPath = getDefaultAuthenticatedPath(user)
  return defaultPath === '/insights'
    || (defaultPath === '/text/conversations' && !canAccessConversationViews(user))
    ? CHROME_COMPANION_INSIGHTS_PATH
    : CHROME_COMPANION_TASKS_PATH
}

const isAllowedChromeCompanionPath = (user, pathname) =>
  pathname === CHROME_COMPANION_TASKS_PATH
  || (pathname === CHROME_COMPANION_SPECIALS_PATH && canAccessLeasingSpecials(user))
  || (canAccessInsights(user) && isChromeCompanionInsightsPathname(pathname))

/**
 * Resolves redirects that can be decided from the requested URL and the
 * already-loaded user. Returning null means the destination route is accepted
 * and may be mounted.
 */
export const resolveAuthenticatedRoute = ({
  companionSearch = '',
  isFramed = false,
  pathname = '',
  search = '',
  user,
}) => {
  if (!user?.id) {
    return null
  }

  if (!canTasksOnlyUserOpenPath(user, pathname)) {
    return { pathname: '/tasks' }
  }

  if (pathname === '/leasing-specials' && !canAccessLeasingSpecials(user)) {
    return { pathname: '/tasks' }
  }

  if (!canAccessInsights(user) && isInsightsPathname(pathname)) {
    return { pathname: '/tasks' }
  }

  const retiredSiteVisitRedirect = getRetiredSiteVisitRedirect(pathname)
  if (retiredSiteVisitRedirect) {
    return retiredSiteVisitRedirect
  }

  const framedCompanionSearch = hasChromeCompanionEmbed(search) ? search : companionSearch
  if (
    isFramed
    && hasChromeCompanionEmbed(framedCompanionSearch)
    && !isChromeCompanionPathname(pathname)
  ) {
    return { pathname: getChromeCompanionPath(user), search: framedCompanionSearch }
  }

  if (isChromeCompanionPathname(pathname)) {
    if (!isChromeCompanionRequest({ pathname, search })) {
      return { pathname: getDefaultAuthenticatedPath(user) }
    }

    const companionPath = getChromeCompanionPath(user)
    // The default chooses the entry view, not the user's permitted destinations.
    if (!isAllowedChromeCompanionPath(user, pathname)) {
      return { pathname: companionPath, search }
    }

    return null
  }

  if (isConversationPathname(pathname) && !canAccessConversationViews(user)) {
    return { pathname: DATA_ONLY_DEFAULT_PATH }
  }

  if (pathname === '/text/announcements' && user.isDataOnlyUser) {
    return { pathname: DATA_ONLY_DEFAULT_PATH }
  }

  if (isSessionEntryPathname(pathname)) {
    return { pathname: getDefaultAuthenticatedPath(user) }
  }

  return null
}
